import { apiFetch } from '../../api/client';
import type { STTMode } from './types';

function isSTTMode(value: unknown): value is STTMode {
  return value === 'native' || value === 'gcp';
}

/**
 * Read the current STT mode from the backend (`GET /stt/mode`). Defaults to
 * `'native'` if the backend is unreachable or returns an unexpected value, so
 * provider selection degrades to the capability-detected default.
 */
export async function fetchSTTMode(): Promise<STTMode> {
  try {
    const response = await apiFetch('/stt/mode');
    if (!response.ok) {
      return 'native';
    }
    const data = (await response.json()) as { mode?: unknown };
    return isSTTMode(data.mode) ? data.mode : 'native';
  } catch {
    return 'native';
  }
}

/**
 * Set the STT mode on the backend (`POST /stt/mode`, available only in local
 * development/test). The same endpoint is driven by curl, so this and curl share
 * one server-side setting.
 */
export async function setSTTMode(mode: STTMode): Promise<STTMode> {
  const response = await apiFetch('/stt/mode', {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
  if (!response.ok) {
    let detail = `Failed to set STT mode (status ${response.status})`;
    try {
      const data = (await response.json()) as { detail?: string };
      if (data.detail) detail = data.detail;
    } catch {
      // non-JSON error body; keep the default message
    }
    throw new Error(detail);
  }
  const data = (await response.json()) as { mode?: unknown };
  return isSTTMode(data.mode) ? data.mode : mode;
}

/**
 * Expose `window.sttMode.get()` / `window.sttMode.set('native'|'gcp')` so the
 * STT mode can be switched from the browser devtools console without typing a
 * curl command. Both routes drive the same backend `/stt/mode` setting.
 */
export function installSTTModeConsoleHelper(): void {
  (
    window as unknown as {
      sttMode?: {
        get: () => Promise<STTMode>;
        set: (mode: STTMode) => Promise<STTMode>;
      };
    }
  ).sttMode = {
    get: fetchSTTMode,
    set: setSTTMode,
  };
}
